/**
 * PubliekeNav — de bovenbalk van alle publieke pagina's, met het mobiele menu
 * en de taalwissel NL | EN.
 *
 * Stond eerder als kopie in zo'n vijftien pagina's (HomeHero, OverOns, Contact,
 * Kennisbank, de juridische pagina's, de inlogpagina ...). Eén component, zodat
 * een nieuw menu-item (Werken bij, 27-10-2026) of de taalwissel overal tegelijk
 * verschijnt. Markup en klassen zijn dezelfde als in de kopieën (HomeHero.css),
 * dus de balk ziet er hetzelfde uit.
 *
 * Props:
 *  - lang ('nl' | 'en'): taal van de pagina. Zet ook <html lang> zolang de
 *    pagina open staat, en kiest de Engelse of Nederlandse menu-links.
 *  - alternatief ({ nl, en }): dezelfde pagina in beide talen; dan verschijnt
 *    de taalwissel. Zonder alternatief geen wissel.
 *  - vast: balk meteen in de "gescrolde" (donkere) stand, voor pagina's met
 *    witte inhoud (werken bij); anders pas na 50px scrollen.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { pad } from '../content/site/paden';
import { siteTeksten } from '../content/site/teksten';
import '../pages/HomeHero.css';
import './PubliekeNav.css';

/** NL | EN. De huidige taal is tekst, de andere een link naar dezelfde pagina. */
export function TaalWissel({ lang, alternatief, label, onKies }) {
  if (!alternatief) return null;
  return (
    <div className="hh-taalwissel" role="group" aria-label={label}>
      {['nl', 'en'].map((taal, i) => (
        <React.Fragment key={taal}>
          {i > 0 && <span className="hh-taalwissel-streep" aria-hidden="true">|</span>}
          {taal === lang ? (
            <span className="hh-taalwissel-actief" aria-current="true" lang={taal}>{taal.toUpperCase()}</span>
          ) : (
            <Link to={alternatief[taal]} hrefLang={taal} lang={taal} onClick={onKies}
                  aria-label={taal === 'en' ? 'English' : 'Nederlands'}>
              {taal.toUpperCase()}
            </Link>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/** <html lang> volgt de taal van de pagina; bij verlaten terug naar wat het was. */
export function useHtmlLang(lang) {
  useEffect(() => {
    const vorige = document.documentElement.lang;
    document.documentElement.lang = lang;
    return () => { document.documentElement.lang = vorige || 'nl'; };
  }, [lang]);
}

function PubliekeNav({ lang = 'nl', alternatief = null, vast = false }) {
  const { currentUser } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const t = siteTeksten(lang).nav;
  const sluit = () => setMenuOpen(false);

  useHtmlLang(lang);

  useEffect(() => {
    if (vast) return undefined;
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [vast]);

  const links = [
    { sleutel: 'overOns', label: t.overOns },
    { sleutel: 'kennisbank', label: t.kennisbank },
    { sleutel: 'werkenBij', label: t.werkenBij },
    { sleutel: 'contact', label: t.contact },
    { sleutel: 'demo', label: t.demo },
  ];
  const knop = currentUser
    ? { to: '/dashboard-new', label: t.dashboard }
    : { to: pad('login', lang), label: t.inloggen };

  return (
    <>
      <nav className={`hh-nav ${vast || isScrolled ? 'hh-nav-scrolled' : ''}`} aria-label={t.hoofdmenu}>
        <div className="hh-nav-inner">
          <Link to={pad('home', lang)} className="hh-logo">
            <img src="/tw-logo-wit-02.svg" alt="TestWisely" className="hh-logo-img" />
          </Link>
          <div className="hh-nav-links">
            {links.map((l) => (
              <Link key={l.sleutel} to={pad(l.sleutel, lang)} className="hh-nav-link">{l.label}</Link>
            ))}
            <TaalWissel lang={lang} alternatief={alternatief} label={t.taal} />
            <Link to={knop.to} className="hh-nav-btn">{knop.label}</Link>
          </div>
          <button
            type="button"
            className={`hh-hamburger ${menuOpen ? 'open' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={t.menu}
            aria-expanded={menuOpen}
          >
            <span></span><span></span><span></span>
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="hh-mobile-overlay" onClick={sluit}>
          <div className="hh-mobile-menu" onClick={(e) => e.stopPropagation()}>
            {links.map((l) => (
              <Link key={l.sleutel} to={pad(l.sleutel, lang)} onClick={sluit}>{l.label}</Link>
            ))}
            <TaalWissel lang={lang} alternatief={alternatief} label={t.taal} onKies={sluit} />
            <Link to={knop.to} className="hh-mobile-btn" onClick={sluit}>{knop.label}</Link>
          </div>
        </div>
      )}
    </>
  );
}

export default PubliekeNav;
